import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { PagerSection, SectionPager } from '../components/layout/SectionPager';
import {
  ROYALTY_PAYABLES_API_URL,
  SALES_INVOICES_API_URL,
  SEARCH_API_URL,
  VOLUME_RUNS_API_URL
} from '../constants';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

const DEMO_WELL_API = '42123400010000';
const DEMO_GROSS = 1000;
const DEMO_PRICE = 70;
const DEMO_SALES = 70000;
const DEMO_ROYALTY = 14000;

interface SearchHit {
  kind: string;
  id: string;
  label: string;
  secondary?: string;
}

interface VolumeRunRow {
  id: string;
  wellId: string;
  periodId: string;
  productionDate: string;
  gross: number;
  unit: string;
}

interface InvoiceRow {
  id: string;
  kind?: string;
  status: string;
  periodId: string;
  amount: number;
  direction?: string;
  volumeRunId?: string;
}

interface DemoMonth {
  run?: VolumeRunRow;
  sales?: InvoiceRow;
  royalty?: InvoiceRow;
}

interface CitationMonthDemoProps {
  authFetch: AuthFetch;
  canPrepare: boolean;
  periodId: string;
  productionDate: string;
  onError: (message: string) => void;
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    throw new Error((await res.text()) || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

function money(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function CitationMonthDemo({
  authFetch,
  canPrepare,
  periodId,
  productionDate,
  onError
}: CitationMonthDemoProps): ReactElement {
  const [month, setMonth] = useState<DemoMonth>({});
  const [busy, setBusy] = useState(false);
  const [readyNote, setReadyNote] = useState('');

  const fetchMonth = useCallback(async (): Promise<DemoMonth> => {
    const [runs, sales, royalty] = await Promise.all([
      readJson<VolumeRunRow[]>(await authFetch(VOLUME_RUNS_API_URL)),
      readJson<InvoiceRow[]>(await authFetch(SALES_INVOICES_API_URL)),
      readJson<InvoiceRow[]>(await authFetch(ROYALTY_PAYABLES_API_URL))
    ]);
    const run = runs.find((r) => r.periodId === periodId && r.gross === DEMO_GROSS);
    const salesRow = sales.find(
      (s) => s.amount === DEMO_SALES && s.periodId === periodId && (!run || !s.volumeRunId || s.volumeRunId === run.id)
    );
    const royaltyRow = royalty.find(
      (s) =>
        s.amount === DEMO_ROYALTY && s.periodId === periodId && (!run || !s.volumeRunId || s.volumeRunId === run.id)
    );
    return { run, sales: salesRow, royalty: royaltyRow };
  }, [authFetch, periodId]);

  const applyMonth = useCallback((next: DemoMonth) => {
    setMonth(next);
    if (next.run && next.sales && next.royalty) {
      setReadyNote(
        `Month is staged: ${next.run.gross} ${next.run.unit} on ${next.run.productionDate}, sales ${money(next.sales.amount)} (${next.sales.status}), royalty ${money(next.royalty.amount)} (${next.royalty.status}).`
      );
    } else {
      setReadyNote('');
    }
  }, []);

  const load = useCallback(async () => {
    applyMonth(await fetchMonth());
  }, [applyMonth, fetchMonth]);

  useEffect(() => {
    void load().catch((e) => onError(e instanceof Error ? e.message : 'Could not load demo month'));
  }, [load, onError]);

  const prepare = async () => {
    setBusy(true);
    onError('');
    try {
      const existing = await fetchMonth();
      applyMonth(existing);
      if (existing.sales?.status === 'Issued' && existing.royalty?.status === 'Issued' && existing.run) {
        setReadyNote(
          `Already staged: ${existing.run.gross} ${existing.run.unit}, sales ${money(existing.sales.amount)}, royalty ${money(existing.royalty.amount)}.`
        );
        return;
      }

      const [wellSearch, partySearch, contractSearch] = await Promise.all([
        readJson<{ wells: SearchHit[] }>(
          await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent(DEMO_WELL_API)}`)
        ),
        readJson<{ parties: SearchHit[] }>(
          await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('Citation Crude')}`)
        ),
        readJson<{ contracts: SearchHit[] }>(
          await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('T-01')}`)
        )
      ]);
      const well = wellSearch.wells[0];
      const billTo = partySearch.parties[0];
      const lease = contractSearch.contracts[0];
      if (!well || !billTo || !lease) {
        throw new Error('Seed well 42123400010000, T-01, or Citation Crude Marketing is missing.');
      }

      let run = existing.run;
      if (!run) {
        run = await readJson<VolumeRunRow>(
          await authFetch(VOLUME_RUNS_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              periodId,
              wellId: well.id,
              contractId: lease.id,
              product: 'Oil',
              productionDate,
              gross: DEMO_GROSS,
              settlementPrice: DEMO_PRICE
            })
          })
        );
      }

      let sales = existing.sales;
      if (!sales) {
        sales = await readJson<InvoiceRow>(
          await authFetch(SALES_INVOICES_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              volumeRunId: run.id,
              billToPartyId: billTo.id,
              amount: DEMO_SALES
            })
          })
        );
      }
      if (sales.status !== 'Issued') {
        sales = await readJson<InvoiceRow>(
          await authFetch(`${SALES_INVOICES_API_URL}/${sales.id}/issue`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accountCode: '4100' })
          })
        );
      }

      let royalty = existing.royalty;
      if (!royalty) {
        const created = await readJson<InvoiceRow[]>(
          await authFetch(ROYALTY_PAYABLES_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ volumeRunId: run.id, pricePerUnit: DEMO_PRICE })
          })
        );
        royalty = created[0];
      }
      if (royalty && royalty.status !== 'Issued') {
        royalty = await readJson<InvoiceRow>(
          await authFetch(`${ROYALTY_PAYABLES_API_URL}/${royalty.id}/issue`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accountCode: '2100' })
          })
        );
      }

      await load();
      setReadyNote(
        `Prepared ${DEMO_GROSS} bbl at ${money(DEMO_PRICE)}/bbl. Sales ${money(DEMO_SALES)} issued to CCM. Royalty ${money(DEMO_ROYALTY)} issued to the mineral owner.`
      );
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Could not prepare the demo month');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="citation-month-demo">
      <section className="card">
        <h2>Citation month — Director walkthrough</h2>
        <p>
          One operated well, one delivery, one settlement price. Sign in as <strong>controller1</strong> /{' '}
          <code>Pass@123</code>. The script below is what you say; the links are what you click.
        </p>
        <p>
          <Link to="/commercial/new">Open Commercial volume</Link>
          {' · '}
          <Link to="/accounting/books">Open Books</Link>
          {' · '}
          <Link to="/accounting/doi">Open Live DOI</Link>
          {' · '}
          <Link to="/contracts">Open Contracts</Link>
          {' · '}
          <Link to="/accounting/accounts">Accounts</Link>
          {' · '}
          <Link to="/accounting/afes">AFEs</Link>
          {' · '}
          <Link to="/accounting/indexes">Indexes</Link>
          {' · '}
          <Link to="/accounting/ppa">PPA</Link>
        </p>
        {canPrepare && (
          <p>
            <button type="button" data-testid="prepare-demo-month" disabled={busy || !periodId} onClick={() => void prepare()}>
              {busy ? 'Preparing…' : 'Prepare this month'}
            </button>
          </p>
        )}
        {readyNote && <p data-testid="demo-month-ready">{readyNote}</p>}
        <table className="requests-grid">
          <thead>
            <tr>
              <th>Item</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Well</td>
              <td>Demo San Andres 1 · API {DEMO_WELL_API}</td>
            </tr>
            <tr>
              <td>Lease</td>
              <td>T-01 · royalty 0.20 · Citation NRI 0.80</td>
            </tr>
            <tr>
              <td>Volume</td>
              <td>
                {DEMO_GROSS.toLocaleString()} bbl · production {productionDate || '—'} · period {periodId || '—'}
              </td>
            </tr>
            <tr>
              <td>Settlement</td>
              <td>{money(DEMO_PRICE)} / bbl on T-20 offtake (5-day NYMEX WTI average, not a live ticker)</td>
            </tr>
            <tr>
              <td>Marketing AR</td>
              <td>
                {money(DEMO_SALES)} to Citation Crude Marketing
                {month.sales ? ` · ${month.sales.status}` : ''}
              </td>
            </tr>
            <tr>
              <td>Royalty AP</td>
              <td>
                {money(DEMO_ROYALTY)} to Demo Mineral Owner
                {month.royalty ? ` · ${month.royalty.status}` : ''}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <SectionPager enabled>
        <PagerSection id="say" title="What you say">
          <p>
            We are walking one Permian month the way Citation books it: production date selects the books month, the
            revenue DOI splits the barrels, the purchaser owes the settlement, and the mineral owner is paid the
            royalty share of that same price.
          </p>
          <p>
            Today the app posts a volume run, then issues a sales invoice and a royalty payable. That is the stand-in
            for the Delivery journal. The next build posts an immutable Receipt/Delivery volume ledger and the Dr/Cr
            journal from the Delivery — not a second set of numbers, the same month.
          </p>
        </PagerSection>
        <PagerSection id="period" title="1. Open period">
          <p>
            Stay on Accounting → Periods. The selected period must be <strong>Open</strong>. Say: production date on
            the ticket — not posting date — puts barrels in this month. The first five business days of the new month
            the prior month can still be Open, so a late ticket dated last month still lands in last month.
          </p>
          <p>
            <Link to="/accounting">Open Periods</Link>
          </p>
        </PagerSection>
        <PagerSection id="doi" title="2. Lease and DOI">
          <p>
            Open Live DOI on T-01. Point to royalty 0.20000000 and Citation NRI 0.80000000. Say: this deck is why 1,000
            bbl becomes 200 royalty barrels and 800 net to Citation. Working interest 0.70 / 0.30 is the cost deck for
            JIB, not royalty.
          </p>
          <p>
            <Link to="/accounting/doi">Open Live DOI</Link>
          </p>
        </PagerSection>
        <PagerSection id="volume" title="3. Volume">
          <p>
            Open Commercial. The staged run is {DEMO_GROSS.toLocaleString()} bbl Oil on Demo San Andres 1 with
            production date {productionDate || 'the first of the Open month'}. Say: that production date is the well
            flow date. If someone later edits it while both months are Open, we reverse and rebook. If the original
            month is Hard-closed, that edit is a PPA — we do not reopen the month.
          </p>
          <p>
            <Link to={month.run ? `/commercial/${month.run.id}` : '/commercial'}>Open Commercial volume</Link>
          </p>
        </PagerSection>
        <PagerSection id="books" title="4. AR and AP">
          <p>
            Open Books. Type the Open month (2026-09) in the Period filter so QA tickets drop away. Then point to
            Issued {money(DEMO_SALES)} Sales AR and Issued {money(DEMO_ROYALTY)} Royalty AP. Same $70. Say: royalty
            inherits the settlement; T-20 averages the first five NYMEX WTI published days. Receipt at the well has no
            money — money posts on Delivery.
          </p>
          <p>
            <Link to="/accounting/books">Open Books</Link>
          </p>
        </PagerSection>
        <PagerSection id="close" title="5. Close">
          <p>
            Back on Periods, Soft close freezes new issues. Hard close is the lock. Do not hard-close during the
            meeting unless you want to show that invoices refuse to issue. The story for a late ticket after Hard close
            is a PPA in the current Open month, referenced to the original production date.
          </p>
          <p>
            <Link to="/accounting">Open Periods</Link>
          </p>
        </PagerSection>
        <PagerSection id="coding" title="6. Account + AFE">
          <p>
            Open Accounts. Point to 4100 Oil sales and 2100 Royalty payable. Say: these are naturals. The well is the
            collector — we did not invent a cost-center chart. Then open AFEs and point to AFE-421234-WO. That is an
            authorization for expenditure. Trade AP 2000 can Issue without it; workover capital 1600 cannot.
          </p>
          <p>
            <Link to="/accounting/accounts">Open Accounts</Link>
            {' · '}
            <Link to="/accounting/afes">Open AFEs</Link>
          </p>
        </PagerSection>
        <PagerSection id="price" title="7. Offtake price">
          <p>
            Open Indexes or T-20. Compute 1,000 bbl for 2026-09-01 — the 5-day NYMEX formula and loaded weekday $70
            quotes should show $70,000. Default 5-day NYMEX through October, Light at gravity 40–60, Nov CMA from 1
            November. A volume run on T-01 still resolves from this offtake. T-01 stays the lease. Royalty inherits
            the settlement.
          </p>
          <p>
            <Link to="/accounting/indexes">Open Indexes</Link>
          </p>
        </PagerSection>
        <PagerSection id="window" title="8. Five-day Open window">
          <p>
            A ticket dated the last day of the prior month still books in that month while we are inside five US
            business days (weekends skipped). After the fifth business day a new normal post is rejected — that late
            ticket becomes a PPA if the month is already Hard-closed.
          </p>
          <p>
            <Link to="/commercial/new">Post a late ticket</Link>
          </p>
        </PagerSection>
        <PagerSection id="ppa" title="9. PPA">
          <p>
            July 2026 is Hard-closed. Issue a +50 bbl PPA into this Open month. Sales $3,500 and royalty $700 land
            here; July originals do not change. Reverse/rebook only works while both months are still Open.
          </p>
          <p>
            <Link to="/accounting/ppa">Open PPA</Link>
          </p>
        </PagerSection>
        <PagerSection id="next" title="What comes next">
          <p>Still designed, not in this walkthrough:</p>
          <ul>
            <li>Immutable volume ledger (Receipt / Delivery) and accounting journal (Dr / Cr) from the Delivery</li>
            <li>Standard fees and deductions before royalty</li>
          </ul>
        </PagerSection>
      </SectionPager>
    </div>
  );
}
